import getUtcStartEndForQuery from '@/utils/getUtcStartEndForQuery'
import type {
  FilterDraft,
  SecurityRecordFilters,
  SecurityOptions,
  SecurityConfigValues,
  ReviewPayload,
  ReleasePayload,
  SecurityConfigPayload,
} from './types'

export const securityPages = ['security-record', 'security-config'] as const
export const allowedSecurityPages = (hasPermission: (key: string) => boolean) =>
  securityPages.filter(hasPermission)

// Isolate the Channel contract from the previous Security query shapes.
export const SECURITY_QUERY_KEY = ['security', 'channel-rules-v1'] as const

// Record statuses a list can be filtered by. Normal review cannot set `released`.
export const RECORD_STATUSES = ['pending', 'reviewed', 'false_positive', 'released'] as const

export function configPayload(
  values: SecurityConfigPayload,
  options: SecurityOptions,
): SecurityConfigPayload {
  const { channel_ids } = values
  if (
    !Array.isArray(channel_ids) ||
    channel_ids.length < 1 ||
    channel_ids.length > 100 ||
    new Set(channel_ids).size !== channel_ids.length ||
    channel_ids.some((id) => !Number.isSafeInteger(id) || id <= 0)
  )
    throw new Error('channelInvalid')
  if (!channel_ids.includes(options.channel_id)) throw new Error('optionsMismatch')
  const limits: Record<string, number> = {}
  const rule_modes: SecurityConfigValues['rule_modes'] = {}

  for (const { key, min, max } of options.limits) {
    const value = values.limits[key]
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max) {
      throw new Error('limitInvalid')
    }
    limits[key] = value
  }
  const attachmentLimit = limits.max_attachment_bytes
  const messageLimit = limits.max_message_bytes
  if (
    typeof attachmentLimit === 'number' &&
    typeof messageLimit === 'number' &&
    attachmentLimit > messageLimit
  )
    throw new Error('attachmentInvalid')

  for (const { key } of options.rule_groups) {
    const mode = values.rule_modes[key]
    if ((mode !== 'block' && mode !== 'preview') || !options.rule_modes.includes(mode)) {
      throw new Error('modeInvalid')
    }
    rule_modes[key] = mode
  }
  return { channel_ids: [...channel_ids], limits, rule_modes }
}

export const defaultConfigValues = (options: SecurityOptions): SecurityConfigValues => {
  const values: SecurityConfigValues = { limits: {}, rule_modes: {} }
  for (const item of options.limits) values.limits[item.key] = item.default
  for (const item of options.rule_groups) values.rule_modes[item.key] = item.default
  return values
}

export const recentFilterDraft = (now = new Date()): FilterDraft => {
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    dates: [start, end],
    channel_id: null,
    action: null,
    status: null,
    risk_code: '',
    keyword: '',
  }
}

export function recordFilters(draft: FilterDraft): SecurityRecordFilters {
  const [from, to] = draft.dates
  if (
    !(from instanceof Date) ||
    !(to instanceof Date) ||
    !Number.isFinite(+from) ||
    !Number.isFinite(+to)
  ) {
    throw new Error('dateInvalid')
  }
  const [start, end] = getUtcStartEndForQuery([from, to])
  if (
    !start ||
    !end ||
    +new Date(end) < +new Date(start) ||
    +new Date(end) - +new Date(start) > 90 * 86400000
  )
    throw new Error('dateInvalid')

  const keyword = draft.keyword.trim()
  const risk_code = draft.risk_code.trim()
  if (keyword && (Array.from(keyword).length < 3 || Array.from(keyword).length > 320))
    throw new Error('keywordInvalid')
  if (risk_code && !/^[a-z_]{1,64}$/.test(risk_code)) throw new Error('riskInvalid')
  if (
    draft.channel_id != null &&
    (!Number.isSafeInteger(draft.channel_id) || draft.channel_id <= 0)
  )
    throw new Error('channelInvalid')
  if (draft.action && !['blocked', 'previewed'].includes(draft.action))
    throw new Error('modeInvalid')
  if (draft.status && !(RECORD_STATUSES as readonly string[]).includes(draft.status))
    throw new Error('statusInvalid')

  return {
    start,
    end,
    ...(draft.channel_id != null ? { channel_id: draft.channel_id } : {}),
    ...(draft.action ? { action: draft.action } : {}),
    ...(draft.status ? { status: draft.status } : {}),
    ...(risk_code ? { risk_code } : {}),
    ...(keyword ? { keyword } : {}),
  }
}

export function reviewPayload(status: string, remark: string): ReviewPayload {
  if (status !== 'pending' && status !== 'reviewed' && status !== 'false_positive')
    throw new Error('statusInvalid')
  if (Array.from(remark).length > 500) throw new Error('remarkInvalid')
  return { status, remark }
}

export function releasePayload(reason: string): ReleasePayload {
  const trimmed = reason.trim()
  if (!trimmed) throw new Error('reasonRequired')
  if (Array.from(trimmed).length > 500) throw new Error('reasonInvalid')
  return { reason: trimmed }
}

// 503 means the release decision was saved but the sync task could not be queued.
export function isDeliveryUnconfirmedError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 503
}

export function isConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const err = error as {
    response?: {
      status?: number
      data?: {
        code?: number | string
        message?: string
      }
    }
    status?: number
    code?: number | string
  }
  return (
    err.response?.status === 409 ||
    err.response?.data?.code === 409 ||
    err.status === 409 ||
    err.code === 409
  )
}

export function requestError(error: unknown, fallback: string, notFound?: string): string {
  if (typeof error === 'string' && error) return error
  const response = (
    error as {
      response?: { data?: { message?: unknown; errors?: Record<string, unknown> }; status?: number }
    }
  )?.response
  const messages: string[] = []
  for (const detail of Object.values(response?.data?.errors ?? {})) {
    for (const value of Array.isArray(detail) ? detail : [detail])
      if (typeof value === 'string') messages.push(value)
  }
  if (typeof response?.data?.message === 'string') messages.unshift(response.data.message)
  if (response?.status === 404 && notFound) messages.unshift(notFound)
  if (messages.length) return [...new Set(messages)].join(' · ')
  if (response?.status) return `${fallback} (HTTP ${response.status})`
  return error instanceof Error ? error.message : fallback
}
